import React, { useEffect, useState } from "react";
import { Box, Divider, FormControl, IconButton, MenuItem, Select, ToggleButton, Tooltip } from "@mui/material";

import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import HighlightIcon from "@mui/icons-material/Highlight";
import CodeIcon from "@mui/icons-material/Code";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import TableChartIcon from "@mui/icons-material/TableChart";
import BorderTopIcon from "@mui/icons-material/BorderTop";
import BorderBottomIcon from "@mui/icons-material/BorderBottom";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import CallSplitIcon from "@mui/icons-material/CallSplit";

import LinkComponent from "./LinkComponent";

const ToolBar = ({ editor }) => {
  const [, forceUpdate] = useState(0);

  /**
   * TipTap v2 doesn't have useEditorState.
   * Force a rerender whenever the editor state changes.
   */
  useEffect(() => {
    if (!editor) return;

    const update = () => forceUpdate((v) => v + 1);

    editor.on("selectionUpdate", update);
    editor.on("transaction", update);
    editor.on("update", update);

    return () => {
      editor.off("selectionUpdate", update);
      editor.off("transaction", update);
      editor.off("update", update);
    };
  }, [editor]);

  if (!editor) return null;

  const headingValue = editor.isActive("heading", { level: 2 }) ? "heading2" : editor.isActive("heading", { level: 3 }) ? "heading3" : editor.isActive("heading", { level: 4 }) ? "heading4" : editor.isActive("heading", { level: 5 }) ? "heading5" : editor.isActive("heading", { level: 6 }) ? "heading6" : "paragraph";

  const handleHeadingChange = (e) => {
    const value = e.target.value;

    if (value === "paragraph") {
      editor.chain().focus().setParagraph().run();
      return;
    }

    const level = parseInt(value.replace("heading", ""), 10);

    editor.chain().focus().setHeading({ level }).run();
  };

  const insertTable = () =>
    editor
      .chain()
      .focus()
      .insertTable({
        rows: 3,
        cols: 3,
        withHeaderRow: true,
      })
      .run();

  const addRow = () => editor.chain().focus().addRowAfter().run();

  const deleteRow = () => editor.chain().focus().deleteRow().run();

  const addColumn = () => editor.chain().focus().addColumnAfter().run();

  const deleteColumn = () => editor.chain().focus().deleteColumn().run();

  const mergeCells = () => editor.chain().focus().mergeCells().run();

  const splitCell = () => editor.chain().focus().splitCell().run();

  const deleteTable = () => editor.chain().focus().deleteTable().run();

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 0.5,
        p: 1,
        borderBottom: 1,
        borderColor: "divider",
        backgroundColor: "background.paper",
      }}
    >
      <FormControl size="small" sx={{ minWidth: 170 }}>
        <Select value={headingValue} onChange={handleHeadingChange}>
          <MenuItem value="paragraph">Paragraph</MenuItem>
          <MenuItem value="heading2">Heading 1</MenuItem>
          <MenuItem value="heading3">Heading 2</MenuItem>
          <MenuItem value="heading4">Heading 3</MenuItem>
          <MenuItem value="heading5">Heading 4</MenuItem>
          <MenuItem value="heading6">Heading 5</MenuItem>
        </Select>
      </FormControl>

      <ToggleButton value="bold" size="small" selected={editor.isActive("bold")} onChange={() => editor.chain().focus().toggleBold().run()}>
        <FormatBoldIcon fontSize="small" />
      </ToggleButton>

      <ToggleButton value="italic" size="small" selected={editor.isActive("italic")} onChange={() => editor.chain().focus().toggleItalic().run()}>
        <FormatItalicIcon fontSize="small" />
      </ToggleButton>

      <ToggleButton value="underline" size="small" selected={editor.isActive("underline")} onChange={() => editor.chain().focus().toggleUnderline().run()}>
        <FormatUnderlinedIcon fontSize="small" />
      </ToggleButton>

      <ToggleButton value="strike" size="small" selected={editor.isActive("strike")} onChange={() => editor.chain().focus().toggleStrike().run()}>
        <StrikethroughSIcon fontSize="small" />
      </ToggleButton>

      <ToggleButton value="highlight" size="small" selected={editor.isActive("highlight")} onChange={() => editor.chain().focus().toggleHighlight({ color: "#fdeb80" }).run()}>
        <HighlightIcon fontSize="small" />
      </ToggleButton>

      <ToggleButton value="code" size="small" selected={editor.isActive("code")} onChange={() => editor.chain().focus().toggleCode().run()}>
        <CodeIcon fontSize="small" />
      </ToggleButton>

      <Divider orientation="vertical" flexItem />

      <ToggleButton value="bullet" size="small" selected={editor.isActive("bulletList")} onChange={() => editor.chain().focus().toggleBulletList().run()}>
        <FormatListBulletedIcon fontSize="small" />
      </ToggleButton>

      <ToggleButton value="ordered" size="small" selected={editor.isActive("orderedList")} onChange={() => editor.chain().focus().toggleOrderedList().run()}>
        <FormatListNumberedIcon fontSize="small" />
      </ToggleButton>

      <ToggleButton value="quote" size="small" selected={editor.isActive("blockquote")} onChange={() => editor.chain().focus().toggleBlockquote().run()}>
        <FormatQuoteIcon fontSize="small" />
      </ToggleButton>

      <Divider orientation="vertical" flexItem />

      {editor.isActive("link") ? (
        <ToggleButton size="small" selected value="unlink" onChange={() => editor.chain().focus().extendMarkRange("link").unsetLink().run()}>
          <LinkOffIcon fontSize="small" />
        </ToggleButton>
      ) : (
        <LinkComponent editor={editor}>
          <ToggleButton value="link" size="small">
            <LinkIcon fontSize="small" />
          </ToggleButton>
        </LinkComponent>
      )}
      <Divider orientation="vertical" flexItem />

      <Tooltip title="Insert Table">
        <IconButton size="small" onClick={insertTable}>
          <TableChartIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Add Row">
        <span>
          <IconButton size="small" disabled={!editor.can().addRowAfter()} onClick={addRow}>
            <BorderBottomIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Delete Row">
        <span>
          <IconButton size="small" disabled={!editor.can().deleteRow()} onClick={deleteRow}>
            <DeleteSweepIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Add Column">
        <span>
          <IconButton size="small" disabled={!editor.can().addColumnAfter()} onClick={addColumn}>
            <ViewColumnIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Delete Column">
        <span>
          <IconButton size="small" disabled={!editor.can().deleteColumn()} onClick={deleteColumn}>
            <DeleteSweepIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Merge Cells">
        <span>
          <IconButton size="small" disabled={!editor.can().mergeCells()} onClick={mergeCells}>
            <CallMergeIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Split Cell">
        <span>
          <IconButton size="small" disabled={!editor.can().splitCell()} onClick={splitCell}>
            <CallSplitIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Delete Table">
        <span>
          <IconButton size="small" disabled={!editor.can().deleteTable()} onClick={deleteTable}>
            <DeleteSweepIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Divider orientation="vertical" flexItem />

      <Divider orientation="vertical" flexItem />

      <Tooltip title="Undo">
        <span>
          <IconButton size="small" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>
            <UndoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Redo">
        <span>
          <IconButton size="small" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>
            <RedoIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default React.memo(ToolBar);
